import React, { useState } from 'react';
import { isEmpty } from 'lodash';
import styled, { css, useTheme } from 'styled-components';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionItemBodyWithAnimation,
  AccordionItemHeading,
  Box,
  Divider,
  ProductCardFeature,
  ProductCardSection,
  useMediaQuery,
} from '@sparknz/spark-pattern-library';

import { Promotions } from '../../typings';
import { getSortedPromoCharacteristics } from '../../utils/planCard.utils';
import { INFORMATION_ICON_TEXT } from '../constants/planCard.constant';

const StyledProductCardFeature = styled(ProductCardFeature)`
  ${({ theme: { spacing } }) => css`
    margin-bottom: ${spacing.s2};
  `}
`;

const Section = styled(ProductCardSection)`
  ${({ theme: { spacing, media } }) => css`
    display: flex;
    flex-direction: column;
    flex-grow: 0;
    padding-bottom: ${spacing.s2};

    ${media.up('slim')} {
      flex-grow: 1;
    }
  `}
`;

interface PlanCardMiddleSectionProps {
  id: string;
  handleInfoIconClick?: (
    contentKey?: string,
    customLink?: string,
  ) => () => void;
  promotions: Promotions;
}

interface PromoCharacteristicsProps {
  fillColor: string;
  promoCharacteristic: Promotions;
  handleInfoIconClick: (contentKey?: string, customLink?: string) => () => void;
}

const PromoCharacteristics = ({
  fillColor,
  promoCharacteristic,
  handleInfoIconClick,
}: PromoCharacteristicsProps) => {
  return (
    <>
      {promoCharacteristic.map(
        ({ contentKey, iconUrl, name, longDescription }) => {
          const customLink = `${name} ${INFORMATION_ICON_TEXT}`;
          return (
            <StyledProductCardFeature
              iconUrl={iconUrl}
              title={name}
              description={longDescription || ''}
              fillColor={fillColor}
              key={name}
              handleInfoIconClick={handleInfoIconClick(contentKey, customLink)}
            />
          );
        },
      )}
    </>
  );
};

const PlanCardMiddleSection = ({
  id,
  handleInfoIconClick,
  promotions,
}: PlanCardMiddleSectionProps) => {
  const {
    palette: {
      baseColors: { kiwi },
    },
  } = useTheme();
  const isMobileView = useMediaQuery({ max: 'lil' });

  const [isAccordionExpanded, setAccordionExpandedItems] = useState(false);

  const accordionHeading = isAccordionExpanded
    ? "Hide what's included"
    : "See what's included";

  const promoCharacteristics = getSortedPromoCharacteristics(promotions);

  if (isEmpty(promoCharacteristics)) {
    return null;
  }

  return (
    <>
      {isMobileView ? (
        <Accordion
          allowMultipleExpanded={false}
          onChange={() => {
            setAccordionExpandedItems((prev: boolean) => !prev);
          }}
        >
          <AccordionItem
            key={id}
            uuid={id}
            isOpen={isAccordionExpanded}
            disableBorder
          >
            <Box mx="s4">
              <AccordionItemHeading>
                {accordionHeading}
                <AccordionButton />
              </AccordionItemHeading>
              <AccordionItemBodyWithAnimation isOpen={isAccordionExpanded}>
                <PromoCharacteristics
                  fillColor={kiwi}
                  promoCharacteristic={promoCharacteristics}
                  handleInfoIconClick={handleInfoIconClick}
                />
              </AccordionItemBodyWithAnimation>
            </Box>
          </AccordionItem>
        </Accordion>
      ) : (
        <Section>
          <PromoCharacteristics
            fillColor={kiwi}
            promoCharacteristic={promoCharacteristics}
            handleInfoIconClick={handleInfoIconClick}
          />
        </Section>
      )}
      <Divider />
    </>
  );
};

export default PlanCardMiddleSection;
